import React from 'react';
import { ArrowDown, UserRound, Briefcase, Mic, GraduationCap, Users, BookOpen, Award, Compass, MessagesSquare, Building2 } from 'lucide-react';
import { Reveal } from '../Reveal';
import { Cta } from '../Cta';
import { useScore } from '../ScoreContext';
const readerPath = ['Find what applies', 'Understand where they are', 'Take a useful next move', 'Indicate what they need'];
const knownContext = ['What matters', 'Where they are stuck', 'Actions they have taken', 'What kind of help may fit'];
const businessPaths = [{
  label: 'Coaching',
  Icon: UserRound
}, {
  label: 'Consulting',
  Icon: Briefcase
}, {
  label: 'Speaking',
  Icon: Mic
}, {
  label: 'Training',
  Icon: GraduationCap
}, {
  label: 'Workshops',
  Icon: Users
}, {
  label: 'Courses',
  Icon: BookOpen
}, {
  label: 'Certification',
  Icon: Award
}, {
  label: 'Advisory',
  Icon: Compass
}, {
  label: 'Community',
  Icon: MessagesSquare
}, {
  label: 'Team implementation',
  Icon: Building2
}];
const enablementGroups = [{
  label: 'Get in the room',
  items: ['Podcast and interview preparation', 'Keynote and speaking angles']
}, {
  label: 'Create the next experience',
  items: ['Workshop outlines', 'Training material', 'CTA paths']
}, {
  label: 'Continue the conversation',
  items: ['Follow-up assets', 'Offer language', 'Sales conversations', 'Content angles']
}];
function Down() {
  return <div className="flex justify-center py-3" data-id="element-872" data-mp="2m0i2hsznl78">
      <ArrowDown className="h-6 w-6 text-seaglass-light/70" aria-hidden="true" data-id="element-873" data-mp="2l8lp4rkhjfw" />
    </div>;
}
function Chips({
  items
}: {
  items: string[];
}) {
  return <div className="mt-3 flex flex-wrap gap-2 md:mt-0" data-id="element-874" data-mp="3ot8egulxkhr4">
      {items.map(item => <span key={item} className="rounded-lg border border-white/12 bg-navy-light/40 px-3 py-1.5 text-sm text-paper/85" data-id="element-875" data-mp="3or5jdw5x7yfj">
          {item}
        </span>)}
    </div>;
}
function Stage({
  n,
  title,
  children
}: {
  n: string;
  title: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-2xl border border-white/12 bg-white/[0.05] p-5 md:grid md:grid-cols-[15rem_1fr] md:items-center md:gap-6 md:p-6" data-id="element-876" data-mp="3kr3ms1f1lv74">
      <div className="flex items-center gap-3" data-id="element-877" data-mp="1lkyet3inukxm">
        <span className="text-sm font-semibold text-seaglass-light/70" data-id="element-878" data-mp="1lj56zkwb9uo1">{n}</span>
        <p className="font-serif text-xl text-paper md:text-2xl" data-id="element-879" data-mp="1gmn2mm4w7yc8">{title}</p>
      </div>
      <div className="mt-3 md:mt-0" data-id="element-880" data-mp="1ckt6mr5n7iie">{children}</div>
    </div>;
}
export function RevenueArchitecture() {
  const {
    openDiagnostic
  } = useScore();
  return <section className="bg-navy px-6 pb-24 pt-12 text-paper md:px-10 md:pb-28 md:pt-16" data-id="element-881" data-mp="297wrrr7knaqs">
      <div className="mx-auto max-w-5xl" data-id="element-882" data-mp="2493d3vgjtdp1">
        <div className="max-w-3xl" data-id="element-883" data-mp="2092xi7hpqwge">
          <Reveal data-id="element-884" data-mp="20819z3jighhs">
            <h2 className="font-serif text-3xl leading-[1.08] text-paper md:text-5xl" data-id="element-885" data-mp="20bp4o60oz9y3">
              A better reader path creates
              <br data-id="element-886" data-mp="ou5yq5vq8svu" />
              <span className="italic text-seaglass-light" data-id="element-887" data-mp="oupdjdq2z15n">
                better business opportunities.
              </span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-paper/70" data-id="element-888" data-mp="jxfgpd0d3n7s">
              Readers get help using the book. You gain the context, assets, and
              pathways to connect the right people to the right work.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} data-id="element-889" data-mp="34ikw2s5iyoh2">
          <div className="mt-12" data-id="element-890" data-mp="34h9tuwrq9os2">
            <Stage n="1" title="The book" data-id="element-891" data-mp="39amg508xpjhn">
              <p className="text-base leading-relaxed text-paper/70 md:text-lg" data-id="element-892" data-mp="39bnq11e7rzxs">
                Ideas, frameworks, stories, language, and offers
              </p>
            </Stage>
            <Down data-id="element-893" data-mp="3j77ifdtkkghs" />
            <Stage n="2" title="A better reader path" data-id="element-894" data-mp="3j6fp69ng2uuo">
              <Chips items={readerPath} data-id="element-895" data-mp="3o6u1w4zzqsz1" />
            </Stage>
            <Down data-id="element-896" data-mp="3o597qghl9c1p" />
            <Stage n="3" title="Known context + intent" data-id="element-897" data-mp="3s8oywxaw2k70">
              <Chips items={knownContext} data-id="element-898" data-mp="3s8fa3d1kyj2w" />
            </Stage>
            <Down data-id="element-899" data-mp="1tu459fi8n5ny" />
            <Stage n="4" title="Fitting business paths" data-id="element-900" data-mp="1tunmeu02dq0x">
              <div className="flex flex-wrap gap-2" data-id="element-901" data-mp="1xuo0uen5y17z">
                {businessPaths.map(({
                label,
                Icon
              }) => <span key={label} className="inline-flex items-center gap-2 rounded-lg border border-seaglass/30 bg-white/[0.06] px-3 py-1.5 text-sm text-paper" data-id="element-902" data-mp="12jg2zrthguhi">
                    <Icon className="h-4 w-4 flex-shrink-0 text-seaglass-light" aria-hidden="true" data-id="element-903" data-mp="1cjgrrta4g2t3" />
                    {label}
                  </span>)}
              </div>
            </Stage>
            <Down data-id="element-904" data-mp="ju0233dvz00k" />
            {/* Dominant final outcome */}
            <div className="rounded-3xl border-2 border-sand bg-sand/15 px-6 py-10 text-center md:py-14" data-id="element-905" data-mp="juj8pny361za">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sand/80" data-id="element-906" data-mp="et3p80bk43j0">
                The destination
              </p>
              <p className="mt-3 font-serif text-3xl font-semibold text-sand md:text-5xl" data-id="element-907" data-mp="osuixzsamei1">
                Better-fit business opportunity
              </p>
            </div>

            {/* Sales-enablement supporting rail, tied to the outcome above */}
            <div className="mt-4 rounded-2xl border border-white/12 border-t-2 border-t-sand/40 bg-white/[0.03] p-6 md:p-7" data-id="element-908" data-mp="3s7yyg64rsnel">
              <p className="text-base font-medium leading-relaxed text-paper/80 md:text-lg" data-id="element-909" data-mp="2cih760tnab7k">
                The same build helps the author create and support those
                opportunities.
              </p>
              <div className="mt-5 grid gap-8 md:grid-cols-3 md:gap-10" data-id="element-910" data-mp="22sfvk8ihal9j">
                {enablementGroups.map(group => <div key={group.label} data-id="element-911" data-mp="2qia5oibexvq3">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sand md:text-[0.8125rem]" data-id="element-912" data-mp="2qjbfykklue6r">
                      {group.label}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2" data-id="element-913" data-mp="2hh27e08wcuw0">
                      {group.items.map(item => <span key={item} className="rounded-lg border border-sand/25 bg-sand/10 px-3 py-1.5 text-[0.9375rem] leading-snug text-paper/85" data-id="element-914" data-mp="2mfvp64v7v81z">
                          {item}
                        </span>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} data-id="element-915" data-mp="3cq5xim4uf2al">
          <div className="mx-auto mt-12 max-w-3xl text-center" data-id="element-916" data-mp="37s4bbsc4emuw">
            <p className="font-serif text-2xl leading-snug text-paper md:text-3xl" data-id="element-917" data-mp="37sme77tafzbs">
              Author Leverage helps the author get more business value from the
              book by making it more useful to readers and more usable across the
              business.
            </p>
            <p className="mt-5 text-base leading-relaxed text-paper/65 md:text-lg" data-id="element-918" data-mp="2u20u5fs3ljcb">
              The reader experience is the mechanism. Author leverage is the
              outcome. Revenue architecture and sales enablement make that
              leverage commercially useful.
            </p>
            <div className="mt-9 flex justify-center" data-id="element-919" data-mp="2g29aozitbaqx">
              <Cta variant="primary" showArrow onClick={openDiagnostic} className="px-10 py-4 text-lg" data-id="element-920" data-mp="ian89bfqh7f8">
                Get Your Reader-to-Revenue Score
              </Cta>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}